/**
 * Resolves a localized value from an object keyed by locale.
 * Used for title, description, ticker text, etc.
 *
 * @param {object|string} obj - { "sk-SK": "...", "en-US": "..." } or plain string (returned as-is)
 * @param {string} locale - Active locale (e.g. "sk-SK", "en-US")
 * @param {string} fallbackLocale - Fallback locale (e.g. "sk-SK")
 * @returns {string} Resolved string or ""
 */
function resolveLocaleValue(obj, locale, fallbackLocale) {
  if (obj == null) return "";
  if (typeof obj === "string") return obj;
  if (typeof obj !== "object") return String(obj);

  const loc = String(locale || "").trim();
  const fall = String(fallbackLocale || "sk-SK").trim();
  const locShort = loc.split("-")[0];
  const fallShort = fall.split("-")[0];

  if (loc && obj[loc] != null && obj[loc] !== "") return String(obj[loc]);
  if (locShort && obj[locShort] != null && obj[locShort] !== "")
    return String(obj[locShort]);
  if (fall && obj[fall] != null && obj[fall] !== "") return String(obj[fall]);
  if (fallShort && obj[fallShort] != null && obj[fallShort] !== "")
    return String(obj[fallShort]);

  const firstKey = Object.keys(obj)[0];
  if (firstKey != null && obj[firstKey] != null) return String(obj[firstKey]);
  return "";
}

/**
 * Resolves the allergens legend array for the given locale.
 *
 * @param {object} allergensLegend - { "sk-SK": [...], "en-US": [...] }
 * @param {string} locale - Active locale
 * @param {string} fallbackLocale - Fallback locale
 * @returns {Array} Array of { number, text } or []
 */
function resolveLegend(allergensLegend, locale, fallbackLocale) {
  if (!allergensLegend || typeof allergensLegend !== "object") return [];

  const loc = String(locale || "").trim();
  const fall = String(fallbackLocale || "sk-SK").trim();
  const locShort = loc.split("-")[0];
  const fallShort = fall.split("-")[0];

  let list =
    allergensLegend[loc] ||
    allergensLegend[locShort] ||
    allergensLegend[fall] ||
    allergensLegend[fallShort];

  const firstKey = Object.keys(allergensLegend)[0];
  if (!Array.isArray(list) && firstKey != null)
    list = allergensLegend[firstKey];

  return Array.isArray(list) ? list : [];
}

/**
 * Resolves ticker text for the given locale.
 *
 * @param {object|string} tickerTextObj - ticker.text: { "sk-SK": "...", "en-US": "..." } or string
 * @param {string} locale - Active locale
 * @param {string} fallbackLocale - Fallback locale
 * @returns {string} Resolved ticker string or ""
 */
function resolveTicker(tickerTextObj, locale, fallbackLocale) {
  return resolveLocaleValue(tickerTextObj, locale, fallbackLocale);
}

export { resolveLocaleValue, resolveLegend, resolveTicker };
